/**
 * Shared Project Interface/Types
 */
import type { useStatsigClient } from "@statsig/react-bindings";
import { type CmpClient } from "@foundation/cmp-client";

import {
    CONTENT_TYPE,
    SUPPORTED_THEMES,
    BUSINESSWEEK_BRAND,
    type TRACKING_TYPE,
} from "~constants";
import type { ComponentViewModel } from "~components/presentational";
import { convertMsToMinSecString } from "~components/shared/card/AudioVideoCard";
import type { HydratedMediaSourceData } from "~components/shared/media/";
import type { MagazineModule } from "~models/transforms/magazineModules";

import type { DropdownLabel } from "./lineup";
import type {
    AbbaVariant as WardenclyffeAbbaVariant,
    Audio as WardenclyffeAudio,
    CuratedItem as WardenclyffeCuratedItem,
    CuratedLink as WardenclyffeCuratedLink,
    CurationModule as WardenclyffeCurationModule,
    CurationModule,
    LineupCuration as WardenclyffeCuration,
    Locale as WardenClyffeLocale,
    Magazine as WardenclyffeMagazine,
    MagazineSection as WardenclyffeMagazineSection,
    NewsletterFragmentFragment,
    PublishedState,
    RatePlan,
    Record as WardenclyffeRecord,
    Security as WardenclyffeSecurity,
    Story as WardenclyffeStory,
} from "./wardenclyffe";

export type Overwrite<T, U> = Pick<T, Exclude<keyof T, keyof U>> & U;

export interface GeoIp {
    countryCode: string;
}

export interface DateTimeValue {
    dateTime: string;
    value: number;
}

export type PlugConfig = {
    [key: string]: string;
};

declare global {
    interface Window {
        newrelic: {
            addPageAction(
                actionName: string,
                customAttributes?: Record<string, unknown>
            ): void;
            noticeError: (
                err: Error | unknown,
                attributes?: Record<string, unknown>
            ) => void;
        };
        NREUM?: {
            noticeError: (
                err: Error,
                attributes: Record<string, unknown>
            ) => void;
            addPageAction(
                actionName: string,
                customAttributes?: Record<string, unknown>
            ): void;
        };
        plugClient: {
            init: (config?: PlugConfig) => () => void | null | undefined;
        };
        initializeAbbaScript: string;
        getCmpClient: () => CmpClient;
        cmpClient: {
            cmd: (() => void)[];
        };
        statsig: ReturnType<typeof useStatsigClient>;
    }
}

export type StringifyArrayObjects<T> = {
    [K in keyof T]: T[K] extends Array<infer U>
        ? U extends object
            ? string[]
            : T[K]
        : T[K];
};

export type StringifyArrayString<T> = {
    [K in keyof T]: T[K] extends string[] ? string : T[K];
};

export interface HomePageProps {
    title: string;
}

export interface NextJsConfig {
    serverRuntimeConfig: Record<never, never>;
    publicRuntimeConfig: { devModeTest: string };
}

export type PublicationState = PublishedState.Published | PublishedState.Draft;
export type CurationSite = "COJP" | "BUSINESS";

type OpinionBrand = "opinion" | "view" | "gadfly";

type Site = "bpol" | "bview" | "bbiz" | "cojp" | "business";

export enum ModuleSource {
    Canonical = "canonical",
    Shadow = "shadow",
}

export enum ZoneColumnCombo {
    "full" = "full",
    "leftRail" = "leftRail",
    "rightRail" = "rightRail",
    "rightyContent" = "rightyContent",
    "leftyContent" = "leftyContent",
    "switchContent" = "switchContent",
    "fullWidthContentWell" = "fullWidthContentWell",
    "leftContentWell" = "leftContentWell",
    "gridRightRail" = "gridRightRail",
    "unsupported" = "unsupported",
}

export type Brand =
    | "business"
    | "markets"
    | "technology"
    | "pursuits"
    | "businessweek"
    | "politics"
    | "cojp"
    | OpinionBrand
    | "green"
    | "citylab"
    | "wealth"
    | "equality"
    | Site;

export type ZoneRegion =
    | "left-rail"
    | "content"
    | "right-rail"
    | "left-content-well"
    | "grid-right-rail"
    | "full-width-content-well"
    | "unsupported";

export enum ZoneType {
    Switch = "switch",
    Righty = "righty",
    Lefty = "lefty",
    Full = "full",
}

export type ZoneKind = `${ZoneType}`;

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

type PickNullable<T> = {
    [P in keyof T as null extends T[P] ? P : never]: T[P];
};

type PickNotNullable<T> = {
    [P in keyof T as null extends T[P] ? never : P]: T[P];
};

export type OptionalNullable<T> = T extends object
    ? {
          [K in keyof PickNullable<T>]?: OptionalNullable<Exclude<T[K], null>>;
      } & {
          [K in keyof PickNotNullable<T>]: OptionalNullable<T[K]>;
      }
    : T;

export type NonNullableFields<T> = {
    [P in keyof T]: NonNullable<T[P]>;
};

type Theme = {
    id?: string;
    url?: string;
};

type ImageTheme = {
    id: string;
    url: string;
};

export enum CuratedItemVariation {
    Navigation = "navigation",
    Featured = "featured",
    FeaturedWithoutByline = "featured_no_byline",
    Imageless = "imageless",
    WithByline = "with_byline",
    WithoutByline = "without_byline",
    Personalized = "personalized",
    TextOverImageDark = "text_over_image_dark",
    TextOverImageLight = "text_over_image_light",
    SmallVerticalVideoPlayer = "small_vertical_video_player",
    WithoutAuthorImage = "without_author_image",
    TextOverImageWithoutAuthorImage = "text_over_without_author_image",
    TextOverImageWithoutByline = "text_over_without_byline",
}

export enum NestedModuleVariation {
    Navigation = "navigation",
    PulseCards = "pulseCards",
    AlternateFirstItems = "alternateFirstItems",
    AlternateItems = "alternateItems",
    Magazine = "magazine",
    Liveblog = "liveblog",
}

export type NavigationItem = {
    headline: string;
    url: string;
};

export interface NavigationProps {
    navItems?: NavigationItem[];
    navTitle?: string;
    navSubtitle?: string;
    navUrl?: string;
}

export interface AutofillData {
    type: string;
    args:
        | {
              [key: string]: unknown;
          }
        | string;
}

export type AbbaVariant = NonNullableFields<WardenclyffeAbbaVariant>;

export type Image = {
    alt?: string;
    baseUrl?: string;
    caption?: string;
    credit?: string;
    height?: number;
    id?: string;
    origHeight?: number;
    origWidth?: number;
    size?: string;
    themes?: ImageTheme[];
    title?: string;
    type?: string;
    url?: string;
    width?: number;
    keywords?: string[];
};

export type Video = {
    aspectRatio?: string;
    autoplay?: boolean;
    bmmrId?: string;
    duration?: number;
    keywords?: string[];
    prerollAdUnit?: string;
    showName?: string;
    thumbnail?: Image;
    title?: string;
    type?: string;
    url?: string;
    isVertical?: boolean;
    isLooping?: boolean;
    hideCaptionByRequest?: boolean;
    hydratedCredit?: string;
    isVideoCurationOverride?: boolean;
};

export type Audio = Overwrite<
    OptionalNullable<WardenclyffeAudio>,
    { url: string }
>;

export type Lede = Image | Video;

export type ExtraAttachment = Image | Video;
interface Show {
    showTitle: string;
}

interface Episode {
    episodeTitle?: string;
    episodeStartTime?: string;
    episodeDescription?: string;
}

export interface Guest {
    guestName: string;
}

export interface LiveSchedule {
    showInfo?: Show;
    episodeInfo: Episode;
    episodeGuests?: Array<Guest>;
}

export interface LiveScheduleResponse {
    liveSchedules: Array<LiveSchedule>;
}

export interface LiveScheduleInfo {
    showTitle: string;
    guests: Array<Guest>;
    episodeInfo?: Episode;
    lineupStreamId?: string;
    livePagePath?: string;
}

export type MagazineCoverImage = {
    alt?: string;
    baseUrl: string;
    caption?: string;
    origHeight: number;
    origWidth: number;
    type: string;
};

export interface CurationImage {
    alt?: string | null;
    baseUrl?: string;
    credit?: string;
    caption?: string | null;
    origWidth?: number;
    origHeight?: number;
    themes?: Theme[] | null;
    type?: string;
}

export interface ImageFromWardenclyffe {
    alt?: string | null;
    url: string;
    caption?: string | null;
    origWidth?: number | null;
    origHeight?: number | null;
    type?: string | null;
    themes?: Theme[];
}

export type NewsletterImage = Overwrite<Image, { placement?: string[] }>;

export type Newsletter = Overwrite<
    OptionalNullable<NewsletterFragmentFragment>,
    {
        defaultEyebrow?: string;
        defaultHeadline?: string;
        images?: NewsletterImage[];
        primaryBrand?: string;
        description?: string;
        sampleUrl?: string;
        signUpList?: string;
        subscriptionType?: string;
        __modelType: "Newsletter";
    }
>;

export interface WardenclyffeModule extends WardenclyffeCurationModule {
    items: (WardenclyffeStory | WardenclyffeCuratedLink)[];
}

export interface Eyebrow {
    text: string;
    url: string;
    bybrowEligible: boolean;
}

export type WithMedia<T> = T extends { type: CONTENT_TYPE.video }
    ? T & { video: Video }
    : T extends { type: CONTENT_TYPE.audio }
    ? T & { audio: Audio }
    : T;

export type AdModule = {
    type: string;
    id: string;
    adType: string;
    variation: string;
};

export interface Person {
    bio?: string;
    editorialBoard?: boolean;
    id: string;
    // eslint-disable-next-line no-use-before-define
    items?: CuratedItem[];
    name: string;
    photoDisplayed: boolean;
    profileUrl?: string;
    roles: string[];
    slug: string;
    title: string;
    thumbnail?: Image;
    twitter?: string;
}

export interface IntradayRangeValue {
    start: string;
    end: string;
}

export interface IntradaySeriesValue {
    series: DateTimeValue[];
    range: IntradayRangeValue;
}

export type Security = Overwrite<
    OptionalNullable<WardenclyffeSecurity>,
    {
        id: string;
        name?: string;
        longName: string;
        intradayPrices?: IntradaySeriesValue;
        priceMovements?: DateTimeValue[];
        intradayPriceMovements?: Pick<IntradaySeriesValue, "series">;
        percentChange1Day: number;
        previousPrice: number;
        price: number;
        priceChange: number;
        mediaSecurityType: string;
        type: string;
        __modelType?: string;
    }
>;

export type RelatedItem = Overwrite<
    OptionalNullable<WardenclyffeStory>,
    {
        autoSummary?: string;
        isHomepage?: boolean;
        ni?: string[];
        audioImage?: Image;
        audios?: Audio[];
        credits?: Person[];
        eyebrow?: Eyebrow;
        headline: string;
        id: string;
        image?: Image;
        keypointsToggle?: boolean;
        lede?: Lede;
        magazineCoverImage?: MagazineCoverImage;
        magazineIssueDate?: string;
        magazineIssueUrl?: string;
        magazineIssueTitle?: string;
        media?: HydratedMediaSourceData;
        newsletter?: Newsletter;
        nicodes?: string[];
        summary?: string;
        url: string;
        showCaption?: boolean;
        videoCurationOverride?: Video;
        terminalTickers?: string[];
        textLabel?: string;
        thumbnail?: Image;
        trackingType: TRACKING_TYPE;
        type: string;
        tickers?: Security[];
        variation?: CuratedItemVariation;
        video?: Video;
        videos?: Video[];
        __modelType: "Story";
    }
>;

export type CuratedLink = Overwrite<
    OptionalNullable<WardenclyffeCuratedLink>,
    {
        headline: string;
        html?: string;
        id: string;
        image?: Image;
        keypointsToggle?: boolean;
        media?: HydratedMediaSourceData;
        labelDropdown?: DropdownLabel;
        relatedStories?: RelatedItem[];
        showCaption?: boolean;
        snapshotTicker?: string;
        snapshotDuration?: string;
        snapshotTitle?: string;
        snapshotStartDate?: string;
        snapshotEndDate?: string;
        summary: string;
        terminalTickers?: string[];
        textLabel?: string;
        trackingType: TRACKING_TYPE;
        type: string;
        url: string;
        variation?: CuratedItemVariation;
        videoCurationOverride?: Video;
        __modelType?: string;
    }
>;

export interface CuratedItemModuleMeta {
    labelUrl?: string;
    subtitle?: string;
    title?: string;
}

export type CuratedStory = Overwrite<
    OptionalNullable<WardenclyffeStory>,
    {
        audioImage?: Image;
        audios?: Audio[];
        byline?: string;
        credits?: Person[];
        eyebrow?: Eyebrow;
        headline: string;
        id: string;
        image?: Image;
        keypointsToggle?: boolean;
        lede?: Lede;
        magazineCoverImage?: MagazineCoverImage;
        magazineIssueDate?: string;
        magazineIssueUrl?: string;
        magazineIssueTitle?: string;
        media?: HydratedMediaSourceData;
        moduleMeta?: CuratedItemModuleMeta;
        newsletter?: Newsletter;
        nicodes?: string[];
        relatedStories?: RelatedItem[];
        summary?: string;
        url: string;
        showCaption?: boolean;
        videoCurationOverride?: Video;
        terminalTickers?: string[];
        textLabel?: string;
        thumbnail?: Image;
        trackingType: TRACKING_TYPE;
        type: string;
        tickers?: Security[];
        variation?: CuratedItemVariation;
        video?: Video;
        videos?: Video[];
        __modelType?: string;
    }
>;

export type CuratedItem = WithMedia<
    Overwrite<
        CuratedLink | CuratedStory,
        {
            autoSummary?: string;
            isHomepage?: boolean;
            ni?: string[];
            nicodes?: string[];
            lede?: Lede;
            moduleMeta?: CuratedItemModuleMeta;
            raw?: WardenclyffeCuratedItem;
            viewModel?: {
                eyebrow?: ComponentViewModel | null;
            };
        }
    >
>;

export type MagazineSection = Overwrite<
    OptionalNullable<WardenclyffeMagazineSection>,
    {
        title: string;
        items: CuratedStory[];
    }
>;

export type MagazineIssue = Overwrite<
    OptionalNullable<WardenclyffeMagazine>,
    {
        id: string;
        title: string;
        date: string;
        displayDate: string;
        coverImage: MagazineCoverImage;
        modules: MagazineSection[];
    }
>;

export interface InfoData {
    name?: string | null;
    url?: string | null;
    disableEyebrow?: boolean;
    isOpinion?: boolean;
}

export interface InfoDataEntry {
    eyebrowData?: InfoData;
    newsletterBrand?: string;
    isCobrand?: boolean;
    isOpinion?: boolean;
}

export interface InfoDataMap {
    [key: string]: InfoDataEntry;
}

export interface PersonFromWardenclyffe {
    id?: string;
    photoDisplayed?: boolean;
    thumbnail?: ImageFromWardenclyffe;
    name?: string;
    editorialBoard?: boolean;
    roles?: string[];
}

export interface TextItem {
    text?: string;
}

export interface HydratedCurationModule {
    abbaConfigKey?: string;
    abbaTestVariants?: AbbaVariant[];
    adPositionIncrement?: number;
    adPositionIncremented?: boolean;
    adType?: string;
    alternateFirstItems?: CuratedItem[];
    alternateItems?: CuratedItem[];
    altLabel?: string;
    altLabelUrl?: string;
    autoplay?: boolean;
    autofillData?: AutofillData;
    callToAction?: string;
    callToActionHyperlink?: string;
    componentName: string;
    duplicateOf?: string;
    editorialTitle?: string;
    editorialTickers?: string[];
    embedHeight?: string;
    embedUrl?: string;
    facebookAccount?: string;
    html?: string;
    id: string;
    image?: Image;
    instagramAccount?: string;
    items: CuratedItem[];
    label?: string;
    labelUrl?: string;
    layout?: string;
    linkedinAccount?: string;
    listId?: string;
    liveStream?: string;
    moduleErrors?: [string] | [];
    moduleIndex?: number;
    modules: HydratedCurationModule[];
    navModule?: NavigationProps;
    nestedVariation?: string;
    newsletter?: Newsletter;
    nextItemOffset?: number;
    pageId: string;
    ratePlan1?: OptionalNullable<RatePlan>;
    ratePlan2?: OptionalNullable<RatePlan>;
    region?: string;
    renderingLocation: ZoneColumnCombo;
    source: ModuleSource;
    sponsored?: boolean;
    storyIdsToExclude?: string;
    streamId?: string;
    subtitle?: string;
    targetCountryCode?: string;
    theme?: SUPPORTED_THEMES | string;
    title?: string;
    tracking?: {
        id: string;
        title: string;
    };
    twitterAccount?: string;
    type: string;
    updatedAt?: string;
    variation: string;
    youtubeAccount?: string;
    zone?: string;
    zoneName: string;
    isMissingShadow?: boolean;
    isShadowWarning?: boolean;
    records?: WardenclyffeRecord[];
}

export interface LiveBlogContributor {
    id: string;
    name: string;
    slug: string | null;
    thumbnail: CurationImage;
    title: string;
    twitter?: string | null;
}

export interface LiveBlogPost {
    content: string;
    contributor?: LiveBlogContributor;
    id: string;
    updatedAt: string;
}

export type LiveBlogState = "closed" | "live";

export interface LiveBlog {
    id: string;
    posts: LiveBlogPost[];
    keypoints?: LiveBlogPost[];
    state: LiveBlogState;
    title: string;
    updatedAt: string;
}

export interface CurationColumn {
    modules: (HydratedCurationModule | AdModule)[];
}

export interface CurationZone {
    name: string;
    kind: ZoneType;
    columns: CurationColumn[];
}

export type Curation = Overwrite<
    OptionalNullable<WardenclyffeCuration>,
    {
        id: string;
        title: string;
        description?: string;
        brand?: Brand;
        path: string;
        theme?: string;
        image?: CurationImage;
        zones: CurationZone[];
        isPreviewLastGoodCopy?: boolean;
    }
>;

export type RelatedItemWithSnapshot = Overwrite<
    RelatedItem,
    {
        embedUrl?: string;
        keypointsToggle?: boolean;
        labelDropdown?: DropdownLabel;
        labelText?: string;
        lede?: Lede;
        meta?: {
            [key: string]: unknown;
        };
        snapshotTicker?: string;
        snapshotDuration?: string;
        snapshotTitle?: string;
        snapshotStartDate?: string;
        snapshotEndDate?: string;
    }
>;

export interface VariationComponent extends HydratedCurationModule {
    alternateFirstItems?: CuratedItem[];
    alternateItems?: CuratedItem[];
    moduleIndex?: number;
    region?: string;
    video?: Video;
    magazineModules?: MagazineModule[];
    isLastInColumn?: boolean;
    infoMode?: boolean;
}

interface PreviewNotesLink {
    name?: string;
    url?: string;
}

export interface PreviewNotes {
    curationName?: string;
    description?: string;
    warning?: string;
    links?: PreviewNotesLink[];
}

export interface PaginatedModule extends HydratedCurationModule {
    nextItemOffset: number;
}

export interface PageData {
    locale: string;
    pageId: string;
    pageNumber: number;
    preview: boolean;
    previousPage: number;
    publishedState: PublicationState;
    shouldPaginate: boolean;
    adCode: string;
    title: string;
    pageType: string;
    brand: string;
    description: string;
    path: string;
}

export type Locale = `${WardenClyffeLocale}`;

export interface AnalyticsData {
    adcode: string;
    brand: string;
    contentType?: string | null;
    pageType?: string | null;
    parselySite?: string | null;
    refresh?: boolean;
    template: string;
    title: string;
    transporter?: boolean;
    vertical?: string | null;
}

export interface MarketMover {
    tickerId: string;
    price: number;
    percentChange: number;
    storyHeadline: string | null;
    storyUrl: string | null;
    assetId: string | null;
}

export type StoryCollection = {
    description: string;
    title: string;
    titleLink: string;
    slug: string;
    stories: CuratedStory[];
};

export type ModulesById = Record<string, CurationModule | null>;
export type HydratedModulesById = Record<string, HydratedCurationModule | null>;

export interface NotFound {
    notFound: boolean;
}

export interface ServerError {
    statusCode?: number;
}

export interface GraphQLError {
    code?: number | undefined;
    extensions?: unknown[];
    locations?: unknown[];
    message?: string | undefined;
    path?: (string | number)[] | undefined;
}

export interface SecurityOverride {
    name?: string;
    url?: string;
    useLastYield?: boolean;
}

export type AdDemand = "extra-low" | "low" | "medium" | "high";

export type SecurityWithRealTimeData = Pick<
    Security,
    "price" | "percentChange1Day"
>;

export type RealTimeData = {
    [key: string]: string | number | boolean | null;
};

export function isCuratedStory(x: CuratedItem): x is CuratedStory {
    return (x as CuratedStory).__modelType === "Story";
}

export function isCuratedLink(x: CuratedItem): x is CuratedLink {
    return (x as CuratedLink).__modelType === "CuratedLink";
}

export function itemHasAudio(x: CuratedItem): boolean {
    return x.type === CONTENT_TYPE.audio && Boolean((x as CuratedStory).audios);
}

export function getAudios(x: CuratedItem): Audio[] | undefined {
    return itemHasAudio(x) ? (x as CuratedStory).audios : undefined;
}

export function getAudio(x: CuratedItem): Audio | undefined {
    const audios = getAudios(x);
    return audios?.length ? audios[0] : undefined;
}

export function getAudioDuration(x?: Audio): number | undefined {
    if (!x) return undefined;

    return parseInt(x.duration || "0", 10);
}

export function getHTML(x: CuratedItem): string | undefined {
    return isCuratedLink(x) ? x.html : undefined;
}

export function itemHasVideoLede(x: CuratedItem): boolean {
    return Boolean((x as CuratedStory).lede?.type === "Video");
}

export function itemIsVideoStory(x: CuratedItem): boolean {
    return Boolean(
        (x as CuratedStory).type === CONTENT_TYPE.video && itemHasVideo(x)
    );
}

export function itemIsVideoStoryOrHasVideoOverride(x: CuratedItem): boolean {
    return Boolean(x.videoCurationOverride || itemIsVideoStory(x));
}

export function getVideoOrVideoCurationOverride(
    x: CuratedItem
): Video | undefined {
    return (x.videoCurationOverride || getVideo(x)) ?? undefined;
}

export function itemHasVideo(x: CuratedItem): boolean {
    return Boolean((x as CuratedStory).video);
}

export function itemHasVideos(x: CuratedItem): boolean {
    return Boolean((x as CuratedStory).videos?.length);
}

export function itemHasAnyVideo(x: CuratedItem): boolean {
    return Boolean(
        x.videoCurationOverride ||
            itemHasVideoLede(x) ||
            itemHasVideo(x) ||
            itemHasVideos(x)
    );
}

export function getLedeVideo(x: CuratedItem): Video | undefined {
    return itemHasVideoLede(x) ? (x as CuratedStory).lede : undefined;
}

export function getAnyVideo(x: CuratedItem): Video | undefined {
    if (itemHasAnyVideo(x)) {
        return (
            x.videoCurationOverride ||
            getLedeVideo(x) ||
            getVideo(x) ||
            getVideos(x)![0]
        );
    }
    return undefined;
}

export function getVideo(x: CuratedItem): Video | undefined {
    return itemHasAnyVideo(x) ? (x as CuratedStory).video : undefined;
}

export function getVerticalVideoOrVideoCurationOverride(
    x: CuratedItem
): Video | undefined {
    const video = getVideoOrVideoCurationOverride(x);
    if (videoIsVertical(video)) {
        return video;
    }
    return undefined;
}

export function getVideos(x: CuratedItem): Video[] | undefined {
    return itemHasAnyVideo(x) ? (x as CuratedStory).videos : undefined;
}

export function getVideoDuration(x?: Video): number | undefined {
    if (!x) return undefined;

    return x.duration;
}

export function itemHasImage(x: CuratedItem): boolean {
    return Boolean(x.image?.baseUrl);
}

export function itemHasMagazineCoverImage(x: CuratedItem): boolean {
    return Boolean((x as CuratedStory).media?.magazineCoverImage?.baseUrl);
}

export function getItemImage(x: CuratedItem): Image | undefined {
    if (!x || !itemHasImage(x)) return undefined;

    return x.image;
}

export function getAudioImage(x: CuratedItem): Image | undefined {
    return x.type === CONTENT_TYPE.audio
        ? (x as CuratedStory).audioImage
        : undefined;
}

export function getNewsletter(x: CuratedItem): Newsletter | undefined {
    return isCuratedStory(x) ? x.newsletter : undefined;
}

export function getByline(x: CuratedItem): string | undefined {
    return isCuratedStory(x) ? x.byline : undefined;
}

export function getFranchise(x: CuratedItem): string | undefined {
    return isCuratedStory(x) ? x.franchise : undefined;
}

export function getBrand(x: CuratedItem): string | undefined {
    return isCuratedStory(x) ? x.brand : undefined;
}

export function getCredits(x: CuratedItem): Person[] | undefined {
    return isCuratedStory(x) ? x.credits : undefined;
}

export function getKeypointsToggle(x: CuratedItem): boolean | undefined {
    return isCuratedStory(x) ? x.keypointsToggle : undefined;
}

export function getPublishedAt(x: CuratedItem): string | undefined {
    return isCuratedStory(x) ? x.publishedAt : undefined;
}

export function getUpdatedAt(x: CuratedItem): string | undefined {
    return isCuratedStory(x) ? x.updatedAt : undefined;
}

export function getEyebrowViewModel(
    x: CuratedItem
): ComponentViewModel | undefined {
    return x.viewModel?.eyebrow || undefined;
}

export function getEyebrow(x: CuratedItem): Eyebrow | undefined {
    return isCuratedStory(x) ? x.eyebrow : undefined;
}

export function getNiCodes(x: CuratedItem): string[] | undefined {
    return isCuratedStory(x) ? x.nicodes : undefined;
}

export function getMagazineCoverImage(
    x: CuratedItem
): MagazineCoverImage | undefined {
    return (x as CuratedStory).magazineCoverImage
        ? (x as CuratedStory).magazineCoverImage
        : undefined;
}

export function getMagazineIssueUrl(x: CuratedItem): string | undefined {
    return (x as CuratedStory).magazineIssueUrl
        ? (x as CuratedStory).magazineIssueUrl
        : undefined;
}

export function getMagazineIssueTitle(x: CuratedItem): string | undefined {
    return (x as CuratedStory).magazineIssueTitle
        ? (x as CuratedStory).magazineIssueTitle
        : undefined;
}

export function getMagazineIssueDate(x: CuratedItem): string | undefined {
    return (x as CuratedStory).magazineIssueDate
        ? (x as CuratedStory).magazineIssueDate
        : undefined;
}

export function getRelatedStories(x: CuratedItem): RelatedItem[] | undefined {
    return x?.relatedStories ? x.relatedStories : undefined;
}

export function getTerminalBlogId(
    x: CuratedItem | RelatedItem
): string | undefined {
    return getMeta(x)?.terminalBlogId;
}

export function getMeta(x: CuratedItem | RelatedItem) {
    return (x as CuratedStory)?.meta ? (x as CuratedStory).meta : undefined;
}

export function itemHasSnapshot(x: CuratedItem): boolean {
    if (!x.snapshotTicker) return false;

    const { snapshotStartDate, snapshotEndDate, snapshotDuration } = x;

    return Boolean(snapshotDuration || (snapshotStartDate && snapshotEndDate));
}

export function moduleHasSubNav(
    x: Pick<VariationComponent, "navModule">
): boolean {
    if (!x.navModule) return false;

    return Boolean(x.navModule.navItems && x.navModule.navItems.length);
}

export function moduleHasItems(x: VariationComponent): boolean {
    return Boolean(x.items && x.items.length);
}

export function getModuleSubNavTitle(
    x: Pick<VariationComponent, "navModule">
): string | undefined {
    if (!moduleHasSubNav(x)) return undefined;

    return x.navModule?.navTitle;
}

export function getModuleSubNavSubtitle(
    x: Pick<VariationComponent, "navModule">
): string | undefined {
    if (!moduleHasSubNav(x)) return undefined;

    return x.navModule?.navSubtitle;
}

export function getModuleSubNavUrl(
    x: Pick<VariationComponent, "navModule">
): string | undefined {
    if (!moduleHasSubNav(x)) return undefined;

    return x.navModule?.navUrl;
}

export function getModuleSubNavItems(
    x: Pick<VariationComponent, "navModule">
): NavigationItem[] | [] {
    if (!moduleHasSubNav(x)) return [];

    return x.navModule?.navItems || [];
}

export function videoIsVertical(video: Video | undefined) {
    return Boolean(video?.aspectRatio === "2:3");
}

export function getTheme(x: VariationComponent): string | undefined {
    return x?.theme;
}

export function getIsOpinionPageTheme(x: VariationComponent["theme"]): boolean {
    return x === SUPPORTED_THEMES.opinionPage;
}

export function isBusinessweekBrand(x: CuratedStory["brand"]): boolean {
    return x === BUSINESSWEEK_BRAND;
}

export function isTextOverMediaItemVariation(
    x?: CuratedItemVariation
): boolean {
    return (
        x === CuratedItemVariation.TextOverImageDark ||
        x === CuratedItemVariation.TextOverImageWithoutAuthorImage ||
        x === CuratedItemVariation.TextOverImageWithoutByline
    );
}

export const getDurationInMs = (item: CuratedStory) => {
    if (item.type !== CONTENT_TYPE.video && item.type !== CONTENT_TYPE.audio) {
        return undefined;
    }
    const video = getVideo(item);
    const audio = getAudio(item);
    const duration = getVideoDuration(video) || getAudioDuration(audio);

    return duration ? convertMsToMinSecString(duration) : undefined;
};
